export const LANGUAGE_CODES = [
  "bs", "en", "de", "fr", "it", "es", "pt", "nl", "pl", "cs", "sk",
  "sl", "hu", "ro", "bg", "el", "da", "sv", "fi", "no", "et", "lv",
  "lt", "ga", "mt", "sr", "hr", "me",
] as const;

export type LanguageCode = (typeof LANGUAGE_CODES)[number];
export const BOSNIAN_CODE: LanguageCode = "bs";
export const MAX_ADDITIONAL_LANGUAGES = 5;

const LANGUAGE_CODE_SET = new Set<string>(LANGUAGE_CODES);

export function isValidLanguageCode(code: string): code is LanguageCode {
  return LANGUAGE_CODE_SET.has(code);
}

/** Public list filter: only non-Bosnian codes are valid URL params. */
export function parsePublicLanguageFilter(raw: unknown): string | undefined {
  if (typeof raw !== "string" || !raw) return undefined;
  if (!isValidLanguageCode(raw) || raw === BOSNIAN_CODE) return undefined;
  return raw;
}

/** API read path: empty junction rows still imply Bosnian baseline. */
export function normalizeAttachedLanguages(codes: string[]): string[] {
  return codes.length > 0 ? codes : [BOSNIAN_CODE];
}

export function getAvailablePublicLanguageFilters(
  therapists: Array<{ languages?: readonly string[] | null }>,
): LanguageCode[] {
  const available = new Set<string>();
  for (const therapist of therapists) {
    for (const code of therapist.languages ?? []) {
      if (code !== BOSNIAN_CODE && isValidLanguageCode(code)) {
        available.add(code);
      }
    }
  }

  return LANGUAGE_CODES.filter((code) => code !== BOSNIAN_CODE && available.has(code));
}

/** UI: drop the implicit Bosnian code so callers only deal with additional languages. */
export function stripBosnian(codes: string[] | null | undefined): string[] {
  return Array.isArray(codes) ? codes.filter((c) => c !== BOSNIAN_CODE) : [];
}

/** Additional languages only — validates, dedupes, caps, strips Bosnian. */
export function sanitizeAdditionalLanguageCodes(codes: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const code of codes) {
    if (code === BOSNIAN_CODE || !isValidLanguageCode(code) || seen.has(code)) continue;
    seen.add(code);
    out.push(code);
    if (out.length >= MAX_ADDITIONAL_LANGUAGES) break;
  }
  return out;
}

/**
 * Maps deprecated `speaksEnglish` to `languages` before Zod validation.
 * When `languages` is omitted: true → ["en"], false → [].
 */
export function applyLegacySpeaksEnglish(body: Record<string, unknown>): void {
  if (typeof body.speaksEnglish !== "boolean") return;
  if (body.languages === undefined) {
    body.languages = body.speaksEnglish ? ["en"] : [];
  }
  delete body.speaksEnglish;
}
